import type { PlanPriceDto } from '../../api/billingPayments'
import { resolvePlanCardsCopy } from '../../billing/planCatalogCopy'

type PlanPricingCardsProps = {
  catalog: PlanPriceDto[]
  currentPlan: string
  onSelectPlan: (plan: PlanPriceDto) => void
  formatRub: (amount: number) => string
}

const SOLD_PLANS = new Set(['start', 'month', 'year'])

const CheckIcon = () => (
  <svg className="market-pricing-card__icon market-pricing-card__icon--yes" viewBox="0 0 16 16" aria-hidden="true">
    <path d="M6.5 11.2 3.3 8l1-1 2.2 2.2 5.5-5.5 1 1-6.5 6.5z" fill="currentColor" />
  </svg>
)

const CrossIcon = () => (
  <svg className="market-pricing-card__icon market-pricing-card__icon--no" viewBox="0 0 16 16" aria-hidden="true">
    <path
      d="M4.5 4.5 8 8m0 0 3.5 3.5M8 8l3.5-3.5M8 8 4.5 11.5"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
    />
  </svg>
)

const priceForPlan = (plan: PlanPriceDto, formatRub: (amount: number) => string) => {
  if (plan.plan === 'start') {
    return {
      value: '0 ₽',
      period: 'навсегда',
      note: 'Для знакомства с кабинетом',
    }
  }
  if (plan.plan === 'year' && plan.yearly_rub != null) {
    return {
      value: formatRub(plan.yearly_rub),
      period: 'в год',
      note: '≈ 2 492 ₽/мес с 2 заведениями — экономия ≈ 30 000 ₽ в год',
    }
  }
  if (plan.plan === 'month') {
    return {
      value: formatRub(plan.monthly_rub),
      period: 'в месяц',
      note: '+1 990 ₽ в месяц за каждое доп. заведение',
    }
  }
  return {
    value: formatRub(plan.monthly_rub),
    period: '/ мес',
    note: null,
  }
}

const ctaLabelForPlan = (plan: string) => {
  switch (plan) {
    case 'month':
      return 'Выбрать Месяц'
    case 'year':
      return 'Выбрать Год'
    default:
      return 'Выбрать тариф'
  }
}

export const PlanPricingCards = ({
  catalog,
  currentPlan,
  onSelectPlan,
  formatRub,
}: PlanPricingCardsProps) => {
  const soldCatalog = catalog.filter((plan) => SOLD_PLANS.has(plan.plan))
  const resolvedCards = resolvePlanCardsCopy(soldCatalog)

  return (
    <section className="market-pricing" aria-labelledby="market-pricing-title">
      <div className="market-pricing__header">
        <div>
          <h2 id="market-pricing-title" className="market-pricing__title">
            Выберите тариф
          </h2>
          <p className="market-pricing__lead">
            Сравните возможности и подключите подходящий план.
            <br />
            Комиссия с сделок на всех тарифах — 0%.
          </p>
        </div>
      </div>

      <div className="market-pricing__grid">
        {soldCatalog.map((planPrice) => {
          const copy = resolvedCards.find((item) => item.plan === planPrice.plan)
          const isCurrent = planPrice.plan === currentPlan
          const isHighlighted = copy?.recommended === true || planPrice.plan === 'year'
          const price = priceForPlan(planPrice, formatRub)

          return (
            <article
              key={planPrice.plan}
              className={[
                'market-pricing-card',
                isHighlighted ? 'market-pricing-card--recommended' : '',
                isCurrent ? 'market-pricing-card--current' : '',
              ]
                .filter(Boolean)
                .join(' ')}
            >
              {planPrice.plan === 'year' ? (
                <p className="market-pricing-card__ribbon">Выгоднее</p>
              ) : null}
              {isCurrent ? <p className="market-pricing-card__badge">Ваш тариф</p> : null}

              <header className="market-pricing-card__header">
                <h3 className="market-pricing-card__name">{planPrice.display_name}</h3>
                <p className="market-pricing-card__tagline">{copy?.tagline ?? planPrice.description}</p>
              </header>

              <div className="market-pricing-card__price-block">
                <p className="market-pricing-card__price">
                  <span className="market-pricing-card__price-value">{price.value}</span>
                  <span className="market-pricing-card__price-period">{price.period}</span>
                </p>
                {price.note ? <p className="market-pricing-card__alt-price">{price.note}</p> : null}
              </div>

              <ul className="market-pricing-card__features">
                {(copy?.features ?? []).map((feature) => (
                  <li
                    key={feature.label}
                    className={[
                      'market-pricing-card__feature',
                      feature.included
                        ? 'market-pricing-card__feature--yes'
                        : 'market-pricing-card__feature--no',
                      feature.highlight ? 'market-pricing-card__feature--highlight' : '',
                    ]
                      .filter(Boolean)
                      .join(' ')}
                  >
                    {feature.included ? <CheckIcon /> : <CrossIcon />}
                    <span>{feature.label}</span>
                  </li>
                ))}
              </ul>

              <footer className="market-pricing-card__footer">
                {isCurrent ? (
                  <button type="button" className="echat-btn echat-btn--ghost" disabled>
                    Текущий тариф
                  </button>
                ) : planPrice.plan === 'start' ? (
                  <p className="market-pricing-card__hint">Базовый тариф для знакомства с сервисом</p>
                ) : (
                  <button
                    type="button"
                    className={
                      planPrice.plan === 'year'
                        ? 'echat-btn echat-btn--primary market-pricing-card__cta'
                        : 'echat-btn echat-btn--ghost market-pricing-card__cta'
                    }
                    onClick={() => onSelectPlan(planPrice)}
                  >
                    {ctaLabelForPlan(planPrice.plan)}
                  </button>
                )}
              </footer>
            </article>
          )
        })}
      </div>
    </section>
  )
}
